import { FilestackImageFit } from 'types/Filestack';
import { getImageSrc } from 'utils/getImageSrc';

import {
  filestackLoadingSrcBlur,
  filestackLoadingSrcQuality,
  filestackLoadingSrcRatio,
} from './filestackLoadingSrcConstants';
import { getFilestackImageSize } from './getFilestackImageSize';

type Args = {
  handle: string | undefined;
  mimetype: string | undefined;
  quality: number;
  format: string | undefined;
  fit: FilestackImageFit | undefined;
  originalSize: { width: number; height: number } | undefined;
  size: { width?: number; height?: number };
  insecureSVG?: boolean;
};

export function getFilestackImageProps({
  handle,
  mimetype,
  quality,
  format,
  fit,
  originalSize,
  size: requestedSize,
  insecureSVG,
}: Args):
  | {
      loadingSrc: string;
      src: string;
      srcSet: string | undefined;
      size: { width: number | undefined; height: number | undefined };
    }
  | undefined {
  if (!handle) return undefined;

  const size = getFilestackImageSize({ originalSize, size: requestedSize });
  const size2x = getFilestackImageSize({
    originalSize,
    size: {
      width: requestedSize.width ? requestedSize.width * 2 : undefined,
      height: requestedSize.height ? requestedSize.height * 2 : undefined,
    },
  });

  const has2xWidth = Boolean(
    size.width && size2x.width && size2x.width > size.width,
  );

  const has2xHeight = Boolean(
    size.height && size2x.height && size2x.height > size.height,
  );

  const has2xSize = has2xWidth || has2xHeight;

  const loadingSrc = getImageSrc({
    handle,
    mimetype,
    quality: filestackLoadingSrcQuality,
    format,
    fit,
    blur: filestackLoadingSrcBlur,
    width: size.width ? size.width * filestackLoadingSrcRatio : undefined,
    height: size.height ? size.height * filestackLoadingSrcRatio : undefined,
  });

  const src = getImageSrc({
    handle,
    mimetype,
    quality,
    format,
    fit,
    width: size.width,
    height: size.height,
    insecureSVG,
  });

  const src2x = has2xSize
    ? getImageSrc({
        handle,
        mimetype,
        quality,
        format,
        fit,
        width: size2x.width,
        height: size2x.height,
        insecureSVG,
      })
    : undefined;

  const srcSet = src2x
    ? [src, src2x].map((str, index) => `${str} ${index + 1}x`).join(', ')
    : undefined;

  return { loadingSrc, src, srcSet, size };
}
